// Pure, framework-free helpers for the engagement lifecycle (WP-X6). No React/service imports so
// they are unit-testable; the pages wire them up.

export interface PartyUser {
  id: number
  name?: string
  email?: string
}
export interface PartyPartner {
  id: number
  companyName?: string
}
export interface PaymentSchedule {
  id: number
  triggerType?: string
  amount?: number | null
  currency?: string | null
  status?: string
}

export type EngagementStatus = 'proposed' | 'agreed' | 'paid' | 'delivered' | 'closed' | 'cancelled'

export interface Engagement {
  id: number
  client?: PartyUser
  expertMember?: PartyUser | null
  expertPartner?: PartyPartner | null
  scope?: string
  agreedAmount?: number | null
  currency?: string | null
  terms?: string
  cancellationTerms?: string
  status?: EngagementStatus | string
  clientConfirmed?: boolean
  expertConfirmed?: boolean
  agreedAt?: string | null
  cancelledAt?: string | null
  paymentSchedules?: PaymentSchedule[]
  myRole?: 'client' | 'expert' | null
  createdAt?: string
  updatedAt?: string
}

interface StatusMeta {
  value: EngagementStatus
  label: string
  color: string
}

// Palette-aligned status colors (maroon / teal / gold / grey).
export const ENGAGEMENT_STATUSES: StatusMeta[] = [
  {value: 'proposed', label: 'Proposed', color: '#978B63'}, // gold — in negotiation
  {value: 'agreed', label: 'Agreed', color: '#163944'}, // teal — terms locked
  {value: 'paid', label: 'Paid', color: '#3F7D5A'},
  {value: 'delivered', label: 'Delivered', color: '#3F7D5A'},
  {value: 'closed', label: 'Closed', color: '#9AA0A6'},
  {value: 'cancelled', label: 'Cancelled', color: '#540008'}, // maroon
]

const BY_VALUE: Record<string, StatusMeta> = ENGAGEMENT_STATUSES.reduce(
  (acc, s) => ({...acc, [s.value]: s}),
  {}
)

export const statusLabel = (status?: string | null): string =>
  (status && BY_VALUE[status]?.label) || 'Proposed'
export const statusColor = (status?: string | null): string =>
  (status && BY_VALUE[status]?.color) || '#978B63'

// Negotiable fields (scope/amount/terms) are editable only while PROPOSED.
export const canEdit = (e?: Engagement | null): boolean => e?.status === 'proposed'
export const isProposed = (e?: Engagement | null): boolean => e?.status === 'proposed'
export const isAgreed = (e?: Engagement | null): boolean => e?.status === 'agreed'

// The expert's display name (member name or partner company).
export const expertDisplay = (e?: Engagement | null): string =>
  e?.expertMember?.name || e?.expertPartner?.companyName || '—'

// The counterparty label from the viewer's role.
export const counterpartyLabel = (e?: Engagement | null): string => {
  if (!e) return '—'
  return e.myRole === 'expert' ? e.client?.name || '—' : expertDisplay(e)
}

// A confirmation is still outstanding — used to prompt the viewer to confirm.
export const iNeedToConfirm = (e?: Engagement | null): boolean => {
  if (!e || e.status !== 'proposed') return false
  return e.myRole === 'client'
    ? !e.clientConfirmed
    : e.myRole === 'expert'
    ? !e.expertConfirmed
    : false
}

// AGREED opens an awaiting_payment schedule — the placeholder state (NO charge; X3 held).
export const awaitingPayment = (e?: Engagement | null): boolean =>
  e?.status === 'agreed' && (e?.paymentSchedules ?? []).some((s) => s.status === 'awaiting_payment')

const CURRENCY_SYMBOLS: Record<string, string> = {usd: '$', eur: '€', gbp: '£'}

export const formatMoney = (amount?: number | null, currency?: string | null): string => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—'
  const code = (currency || 'usd').toLowerCase()
  const symbol = CURRENCY_SYMBOLS[code]
  const value = Number(amount).toLocaleString()
  return symbol ? `${symbol}${value}` : `${code.toUpperCase()} ${value}`
}
